import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIsEnglishRoute } from "@/components/LocalizedLink";

/**
 * Runtime-översättning av de långa säljsidorna (/egenkontrollparm, /foretag,
 * /digitala-tjanster) som har sin brödtext direkt i JSX. Ordlistan slår på
 * exakt textinnehåll, laddas bara när ett annat språk än svenska är valt och
 * påverkar aldrig serverrenderad svenska (SEO) eller redan i18n-översatt UI.
 */
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);

export function AutoTranslate() {
  const { i18n } = useTranslation();
  const englishRoute = useIsEnglishRoute();
  const lang = englishRoute ? "en" : i18n.language;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!lang || !lang.startsWith("en")) return;

    let cancelled = false;
    let observer: MutationObserver | undefined;

    void import("@/i18n/sales-en.json").then((mod) => {
      if (cancelled) return;
      const dict = (mod.default ?? mod) as Record<string, string>;

      const translateNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const parent = node.parentElement;
          if (!parent || SKIP_TAGS.has(parent.tagName)) return;
          const raw = node.nodeValue ?? "";
          const trimmed = raw.trim();
          if (trimmed.length < 2) return;
          const hit = dict[trimmed];
          if (!hit) return;
          node.nodeValue = raw.replace(trimmed, hit);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as Element;
        if (SKIP_TAGS.has(el.tagName)) return;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) nodes.push(walker.currentNode as Text);
        nodes.forEach(translateNode);
        for (const target of [el, ...Array.from(el.querySelectorAll("[placeholder],[aria-label],[title],[alt]"))]) {
          for (const attr of ["placeholder", "aria-label", "title", "alt"]) {
            const value = target.getAttribute(attr);
            const hit = value ? dict[value.trim()] : undefined;
            if (hit) target.setAttribute(attr, hit);
          }
        }
      };

      const run = () => {
        observer?.disconnect();
        translateNode(document.body);
        observer?.observe(document.body, { childList: true, subtree: true, characterData: true });
      };

      observer = new MutationObserver((records) => {
        observer?.disconnect();
        for (const r of records) {
          if (r.type === "characterData") translateNode(r.target);
          else r.addedNodes.forEach(translateNode);
        }
        observer?.observe(document.body, { childList: true, subtree: true, characterData: true });
      });

      run();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [lang]);

  return null;
}

export default AutoTranslate;
